import { useCallback, useMemo } from 'react';

import { ApiMethod } from '../models/api-types.type';
import { useAuthContext } from './use-auth-context.hook';

/** Saved rooms and projects, for the vendor (pms-api-v2 `services/pms/vendors/me/saved-rooms`). */

export interface SavedRoomProduct {
    productUuid: string;
    surface?: string;
    liked: boolean;
    name?: string;
    thumbnail?: string;
    price?: { amount: number; currency: string; unit: string } | null;
}

export interface SavedRoomComeback {
    consent: boolean;
    consentAt?: string;
    lastSentAt?: string;
    sentCount: number;
    lastCheckAt?: string;
    unsubscribedAt?: string;
    lastError?: string;
}

export interface SavedRoomRow {
    uuid: string;
    name: string;
    /** Only when the shopper shared the room or said yes to the comeback email; `hasEmail` says one exists. */
    email: string | null;
    hasEmail: boolean;
    /** The shopper shares this room with the shop. Otherwise the pictures, video and share are held back (null). */
    shared: boolean;
    sharedWithVendorAt: string | null;
    visitorId: string | null;
    leadId: string | null;
    leadName: string | null;
    roomImageUrl?: string | null;
    renderUrl?: string | null;
    products: SavedRoomProduct[];
    areaM2: number | null;
    areaApproximate: boolean;
    lastVisitAt?: string;
    createdAt?: string;
    comeback: SavedRoomComeback;
    video: { status: 'none' | 'running' | 'ready' | 'failed'; url?: string; finishedAt?: string };
    project: { uuid: string; name: string; clientName?: string } | null;
    source: 'shopper' | 'pro';
    /** Deleted on this date unless the shopper comes back (12 months with an email, 90 days without). */
    expiresAt: string | null;
}

export interface TakeoffRow {
    productUuid: string;
    name: string;
    thumbnail?: string;
    rooms: Array<{ roomUuid: string; roomName: string; surface: string; areaM2: number | null }>;
    areaM2: number | null;
    areaRange: [number, number] | null;
    confidence: 'confirmed' | 'estimate' | 'rough' | null;
    wastePct: number;
    m2Needed: number | null;
    boxes?: number;
    m2Delivered?: number;
    unitPrice?: { amount: number; currency: string; unit: string };
    total?: number;
    totalRange?: [number, number];
    currency?: string;
}

export interface Takeoff {
    rows: TakeoffRow[];
    likedOnly: Array<{ productUuid: string; name: string }>;
    totals: Array<{ currency: string; total: number; range: [number, number] }>;
    areaM2: number;
    unpriced: number;
    approximate: boolean;
}

export interface SavedRoomDetail extends SavedRoomRow {
    areas: Array<{ surface: string; areaM2: number; isLowerBound: boolean; confidence: string }>;
    takeoff: Takeoff;
    links: Array<{ createdAt: string; expiresAt: string; usedAt?: string }>;
    emailVerifiedAt?: string;
    sharingId?: string | null;
    shareUrl: string | null;
}

export interface SavedRoomsList {
    total: number;
    stats: { rooms: number; withEmail: number; comebackConsent: number; comebackSent: number; projects: number; shared: number };
    rows: SavedRoomRow[];
}

export interface RoomProjectRow {
    uuid: string;
    name: string;
    clientName: string | null;
    notes: string | null;
    rooms: string[];
    email: string | null;
    leadId: string | null;
    areaM2: number;
    lastVisitAt?: string;
    /** Pictures of the rooms the shopper shared; none of the private ones. */
    thumbnails: string[];
    sharedRooms: number;
    hasEmail: boolean;
    updatedAt?: string;
}

export interface ComebackPreview {
    /** `shared: false`: the email shows a neutral frame where the shopper sees their own room. */
    room: { uuid: string; name: string; email: string | null; hasEmail?: boolean; shared?: boolean };
    eligible: boolean;
    reasons: string[];
    sample: boolean;
    since: string;
    matches: Array<{ uuid: string; name: string; thumbnail?: string; reasons: string[]; score: number }>;
    subject: string;
    html: string;
}

export interface SavedRoomsSettings {
    enabled: boolean;
    comebackEmail: boolean;
    roomVideo: boolean;
    smtpConfigured: boolean;
    postalAddress: boolean;
    videoCredits: number;
    videoCostUsd: number;
}

export type RoomsFilter = 'all' | 'shared' | 'email' | 'comeback' | 'video' | 'project';

const BASE = '/services/pms/vendors/me/saved-rooms';

export const useSavedRoomsApi = () => {
    const { sendAuthGuardedRequest } = useAuthContext();

    const rooms = useCallback(
        async (q: { search?: string; filter?: RoomsFilter; limit?: number; skip?: number } = {}): Promise<SavedRoomsList> => {
            const query = new URLSearchParams();
            if (q.search) query.set('search', q.search);
            if (q.filter && q.filter !== 'all') query.set('filter', q.filter);
            if (q.limit) query.set('limit', String(q.limit));
            if (q.skip) query.set('skip', String(q.skip));
            const suffix = query.toString() ? `?${query}` : '';
            return (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/rooms${suffix}`)) as SavedRoomsList;
        },
        [sendAuthGuardedRequest],
    );

    const room = useCallback(
        async (uuid: string): Promise<SavedRoomDetail> => (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/rooms/${uuid}`)) as SavedRoomDetail,
        [sendAuthGuardedRequest],
    );

    const comebackPreview = useCallback(
        async (roomUuid?: string): Promise<ComebackPreview> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, roomUuid ? `${BASE}/rooms/${roomUuid}/comeback-preview` : `${BASE}/comeback-preview`)) as ComebackPreview,
        [sendAuthGuardedRequest],
    );

    const projects = useCallback(
        async (): Promise<RoomProjectRow[]> => ((await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/projects`)) ?? []) as RoomProjectRow[],
        [sendAuthGuardedRequest],
    );

    const takeoff = useCallback(
        async (projectUuid: string): Promise<Takeoff> => (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/projects/${projectUuid}/takeoff`)) as Takeoff,
        [sendAuthGuardedRequest],
    );

    const boardPdf = useCallback(
        async (projectUuid: string): Promise<ArrayBuffer> =>
            (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/projects/${projectUuid}/board.pdf`, undefined, { responseType: 'arraybuffer' } as any)) as ArrayBuffer,
        [sendAuthGuardedRequest],
    );

    const settings = useCallback(
        async (): Promise<SavedRoomsSettings> => (await sendAuthGuardedRequest(ApiMethod.GET, `${BASE}/settings`)) as SavedRoomsSettings,
        [sendAuthGuardedRequest],
    );

    const updateSettings = useCallback(
        async (patch: Partial<Pick<SavedRoomsSettings, 'enabled' | 'comebackEmail' | 'roomVideo'>>): Promise<SavedRoomsSettings> =>
            (await sendAuthGuardedRequest(ApiMethod.PATCH, `${BASE}/settings`, patch)) as SavedRoomsSettings,
        [sendAuthGuardedRequest],
    );

    return useMemo(
        () => ({ rooms, room, comebackPreview, projects, takeoff, boardPdf, settings, updateSettings }),
        [rooms, room, comebackPreview, projects, takeoff, boardPdf, settings, updateSettings],
    );
};
